/**
 * A field component that lets users select a property path from an entity schema.
 *
 * Resolves the schema from an explicit `entitySchema` object, an `entityType` URI, or
 * the current entity in a `FramesManager` context (in that priority order). Once resolved,
 * it traverses the schema up to N levels deep and surfaces matching properties as
 * select options filtered by `includeTypes` and an optional `propertyFilter` callback.
 *
 * The `onChange` callback receives either a plain path string (when the bound data schema
 * type is `'string'`) or a structured `IOption`-shaped object with `path`, `label`,
 * `propertyType`, `definitionType`, `schemaRef`, and `format`.
 */
import _ from 'lodash'
import React from 'react'

import apis from 'browser/app/models/apis'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { SelectField } from 'browser/components/atomic-elements/molecules/fields/select-field'
import {
  PropertyOptionRenderer,
  PropertyValueRenderer,
} from './property-option-card'
import {
  JSONSchemaResolver,
  RefResolver,
  SchemaResolver,
} from 'shared-libs/resolvers/json-schema-resolver'
import {
  getOptionsFromSchemaDeep,
  IOption,
  PropertyType,
} from 'shared-libs/models/utils'
import { FramesManager } from 'shared-libs/components/view/frames-manager'

/**
 * @uiComponent
 */
interface IPropertyPickerProps extends IBaseProps {
  /**
   * The entity schema to extract paths from
   */
  entitySchema?: any

  /**
   * The entity type URI (e.g., '/1.0/entities/metadata/order.json')
   * Will be resolved to get the schema if entitySchema is not provided
   * If not provided, will attempt to get from current entity in frames
   */
  entityType?: string | { entityId: string }

  /**
   * FramesManager instance to access current entity context
   */
  frames?: FramesManager

  /**
   * Whether to include core properties like createdBy
   */
  includeCoreProperties?: boolean

  /**
   * Custom filter function to determine which properties to include
   */
  propertyFilter?: (property: any, path: string) => boolean

  /**
   * Filter by property type. Can be a single type or array of types.
   * Available types: 'string', 'number', 'integer', 'boolean', 'edge', 'object', 'ref'
   * Examples:
   *   - 'string' - only string fields
   *   - ['string', 'number'] - string and number fields
   *   - 'edge' - only entity relationships
   */
  includeTypes: PropertyType[]

  /**
   * When filtering by 'ref' type, this can be used to specify which referenced definitions to include as options.
   * If not provided, all $ref definitions will be included as options.
   */
  includeDefinitions?: string[]

  /**
   * Callback when a path is selected
   */
  onChange?: (selectedOption: string | IOption) => void

  /**
   * Current selected value
   */
  value?: any

  /**
   * Field label
   */
  label?: string

  /**
   * Maximum depth to traverse the schema when resolving nested properties. Defaults to 3.
   */
  depth?: number

  /**
   * When true, the nested schema definition is included in the stored value so the
   * server can emit a complete schema to the AI. Works for object, $ref, array-object,
   * and array-ref picks. Defaults to false (input use case — not needed).
   */
  withNestedSchema?: boolean

  /**
   * Whether the field is disabled
   */
  isDisabled?: boolean

  /**
   * Placeholder text
   */
  placeholder?: string
}

interface IPropertyPickerState {
  options: any[]
  isLoading: boolean
  error?: string
}

const ENTITY_SCHEMA_URI = '/1.0/entities/metadata/entity.json'

export class PropertyPicker extends React.Component<IPropertyPickerProps, IPropertyPickerState> {
  private resolver: RefResolver & SchemaResolver
  // determine if the current value is a string or not
  // if it is a string type, then return the schema property path
  // if it is an object type, then return the full schema property object
  private isValueTypeString: boolean = false

  public static defaultProps: Partial<IPropertyPickerProps> = {
    depth: 3,
    withNestedSchema: false,
    includeCoreProperties: true,
    label: 'Field',
    placeholder: 'Select a field',
    includeTypes: ['string', 'number', 'integer', 'boolean', 'ref'],
    includeDefinitions: ['fileSet'],
  }

  constructor(props: IPropertyPickerProps) {
    super(props)
    this.state = {
      options: [],
      isLoading: false,
      error: undefined,
    }
    this.resolver = new JSONSchemaResolver(apis)
    this.isValueTypeString = props.frames?.getContext('dataSchema')?.type === 'string'
  }

  public async componentDidMount() {
    await this.loadOptions()
  }

  public async componentDidUpdate(prevProps: IPropertyPickerProps) {
    const { entitySchema, entityType, includeTypes, includeDefinitions, frames } = this.props
    if (
      prevProps.entitySchema !== entitySchema ||
      prevProps.entityType !== entityType ||
      prevProps.includeDefinitions !== includeDefinitions ||
      !_.isEqual(prevProps.includeTypes, includeTypes)
    ) {
      await this.loadOptions()
    }
  }

  public render() {
    const { label, value, isDisabled, placeholder, onChange } = this.props
    const { options, isLoading, error } = this.state

    const displayPlaceholder = error
      ? 'Error loading fields'
      : isLoading
        ? 'Loading fields...'
        : placeholder

    return (
      <>
        <SelectField
          isDisabled={isDisabled || isLoading}
          label={label}
          onChange={this.handleChange}
          options={options}
          optionLabelPath="label"
          optionValuePath="path"
          optionRenderer={PropertyOptionRenderer}
          valueRenderer={PropertyValueRenderer}
          placeholder={displayPlaceholder}
          value={value}
          styles={{
            option: (base:any) => ({ ...base, padding: '4px 4px' }),
          }}
        />
        {error && (
          <div style={{ color: 'red', fontSize: '0.875rem', marginTop: '0.25rem' }}>{error}</div>
        )}
      </>
    )
  }

  private handleChange = (option, fullOption) => {
    // Destructure nestedSchema out — it's internal to the picker, not stored directly
    const { label, path, propertyType, definitionType, ref, format, nestedSchema, ...restOption } = fullOption
    // array-* is an internal picker type; store the canonical 'array' value
    const storedPropertyType = propertyType?.startsWith('array-') ? 'array' : propertyType
    // depend on value type, either return just the path or the full schema property object
    const selectedValue = this.isValueTypeString
      ? option
      : {
          ...restOption,
          label,
          path,
          propertyType: storedPropertyType,
          definitionType,
          schemaRef: ref,
          format,
          // Spread the pre-built JSON Schema fragment (properties, items, etc.) when requested
          ...(this.props.withNestedSchema && nestedSchema ? nestedSchema : {}),
        }
    this.props.onChange?.(selectedValue)
  }

  private loadOptions = async () => {
    const {
      depth,
      entitySchema,
      entityType,
      includeCoreProperties,
      includeDefinitions,
      propertyFilter,
      includeTypes,
      frames,
    } = this.props

    let resolvedEntityType = _.get(entityType, 'entityId', entityType)

    // If entityType is not provided, try to get it from the current entity in frames
    if (!resolvedEntityType && !entitySchema && frames) {
      try {
        const entity = frames.getValue('entity')
        resolvedEntityType = entity?.entityType
      } catch (err) {
        console.warn('PropertyPicker: Unable to get entity type from frames:', err)
      }
    }

    if (!entitySchema && !resolvedEntityType) {
      const errorMsg =
        'PropertyPicker: either entitySchema, entityType, or frames with current entity must be provided'
      console.warn(errorMsg)
      this.setState({ error: errorMsg, options: [], isLoading: false })
      return
    }

    this.setState({ isLoading: true, error: undefined })

    try {
      let options: any[] = []
      let schema = entitySchema

      // If entityType is provided but no entitySchema, resolve the schema
      if (!schema && resolvedEntityType) {
        schema = apis.getStore().getRecord(resolvedEntityType)
      }

      if (schema) {
        // If schema has references, resolve them
        try {
          const schemas = await this.resolver.resolveSchema(schema)
          _.forEach(schemas, (resolvedSchema, key) => {
            if (key !== ENTITY_SCHEMA_URI) {
              const schemaOptions = getOptionsFromSchemaDeep(
                resolvedSchema,
                apis,
                this.resolver,
                {
                  includeTypes,
                },
                depth
              )
              options = _.concat(options, schemaOptions)
            }
          })
        } catch (err) {
          console.warn('Failed to resolve schema references, using core properties only:', err)
        }

        // Get options from the main schema
        const mainSchemaOptions = getOptionsFromSchemaDeep(
          schema,
          apis,
          this.resolver,
          {
            includeTypes,
          },
          depth
        )
        options = _.concat(options, mainSchemaOptions)

        // Add core properties if requested
        if (includeCoreProperties) {
          options.push({
            entityType: '/1.0/entities/metadata/user.json',
            label: 'Created By',
            path: 'createdBy',
            propertyType: 'edge',
          })
        }

        // Apply custom filter if provided
        if (propertyFilter) {
          options = _.filter(options, (option) => propertyFilter(option, option.path))
        }

        // Apply property type filter if provided
        if (includeTypes) {
          options = _.filter(options, (option) => {
            // Handle array types (e.g., 'array-string' should match 'string' filter)
            const baseType = option.propertyType?.startsWith('array-')
              ? option.propertyType.replace('array-', '')
              : option.propertyType
            const matchesType =
              includeTypes.includes(option.propertyType) || includeTypes.includes(baseType)
            const matchesRef =
              option.propertyType === 'ref' &&
              includeTypes.includes('ref') &&
              (_.isNil(includeDefinitions) || includeDefinitions?.includes(option.definitionType))

            return matchesType || matchesRef
          })
        }

        // Remove duplicates by path
        options = _.uniqBy(options, 'path')

        // Sort by label
        options = _.sortBy(options, 'label')
      }

      this.setState({ options, isLoading: false, error: undefined })
    } catch (error) {
      console.error('Error loading schema path options:', error)
      const errorMsg = error instanceof Error ? error.message : 'Failed to load field options'
      this.setState({ options: [], isLoading: false, error: errorMsg })
    }
  }
}
